export type PrintPageOptions = {
  title: string;
  bodyHtml: string;
  extraCss?: string;
  showPrintActions?: boolean;
  autoPrint?: boolean;
  explanationText?: string;
};

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Open a new window synchronously (must be called in a user gesture) to avoid popup blocking.
 */
export function openPrintWindowSync(): Window | null {
  return window.open("", "_blank");
}

/**
 * Renders a full HTML print page into the target window.
 */
export function renderPrintPageInto(
  targetWindow: Window | null | undefined,
  options: PrintPageOptions,
): void {
  const {
    title,
    bodyHtml,
    extraCss = "",
    showPrintActions = true,
    autoPrint = true,
    explanationText = "The print dialog will open automatically. If your browser blocks automatic printing, use the button to print.",
  } = options;

  const printHtml = `<!DOCTYPE html>
<html lang="en">
  <meta charset="UTF-8">
  <head>
    <title>${escapeHtml(title)}</title>
    <style>
      body { font-family: system-ui, sans-serif; font-size: 11px; padding: 16px; }
      .print-actions { position: fixed; top: 10px; right: 10px; z-index: 9999; }
      @media print { .print-actions { display: none !important; } }
      p { margin-bottom: 0; }
      ${extraCss}
    </style>
  </head>
  <body>
    ${showPrintActions ? `<div class="print-actions"><button onclick="window.print()">Print</button><p class="small">${escapeHtml(explanationText)}</p></div>` : ""}
    ${bodyHtml}
    <script>
      (function() {
        if (${autoPrint}) setTimeout(function(){ try { window.print(); } catch (e) {} }, 0);
      })();
    </script>
  </body>
</html>`;

  if (targetWindow && !targetWindow.closed) {
    try {
      targetWindow.document.open();
      targetWindow.document.write(printHtml);
      targetWindow.document.close();
      targetWindow.focus();
      return;
    } catch {
      const blob = new Blob([printHtml], {type: "text/html; charset=utf-8"});
      const url = URL.createObjectURL(blob);
      targetWindow.location.href = url;
      targetWindow.focus();
      return;
    }
  }

  const blob = new Blob([printHtml], {type: "text/html; charset=utf-8"});
  window.location.href = URL.createObjectURL(blob);
}
